import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Search, ChevronDown } from "lucide-react";

/* Centralized species-dependent, searchable breed selector (Phase 7A).
   Binds into a form value object `v`. Writes species, species_id, breed, breed_id,
   breed_canonical. Prefers an atomic multi-field patch via `setMany(patch)` so that
   switching species cannot be clobbered by a stale-snapshot parent updater (the bug
   that left non-dog species showing dog breeds). Falls back to per-key `set(k, val)`
   for callers whose setter is already functional (e.g. FormModal). */
export function BreedSelect({ v, set, setMany }) {
  const [species, setSpecies] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [q, setQ] = useState(v.breed || "");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const speciesId = v.species_id || "";

  const apply = (patch) => {
    if (setMany) setMany(patch);
    else Object.entries(patch).forEach(([k, val]) => set(k, val));
  };

  useEffect(() => { api.get("/pet-master/species").then((r) => setSpecies(r.data.species || [])); }, []);
  // Keep the visible search box in sync when the bound breed changes externally
  // (e.g. species switch clears it, or Edit Pet loads an existing breed).
  useEffect(() => { setQ(v.breed || ""); }, [v.breed]);
  useEffect(() => {
    if (!speciesId) { setBreeds([]); return; }
    const t = setTimeout(() => {
      api.get(`/pet-master/breeds?species_id=${speciesId}&search=${encodeURIComponent(q)}`)
        .then((r) => setBreeds(r.data.breeds || []));
    }, 150);
    return () => clearTimeout(t);
  }, [speciesId, q]);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pickSpecies = (s) => {
    setQ("");
    apply({ species: s.name, species_id: s.id, breed: "", breed_id: "", breed_canonical: "" });
  };
  const pickBreed = (b) => {
    setQ(b.name); setOpen(false);
    apply({ breed: b.name, breed_id: b.generic ? "" : b.id, breed_canonical: b.name });
  };

  return (
    <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-slate-500 mb-1 block">Species</Label>
        <select data-testid="breedselect-species" value={speciesId}
          onChange={(e) => { const s = species.find((x) => x.id === e.target.value); if (s) pickSpecies(s); }}
          className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm min-h-[44px]">
          <option value="">Select species</option>
          {species.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div ref={boxRef} className="relative">
        <Label className="text-xs text-slate-500 mb-1 block">Breed</Label>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input data-testid="breedselect-breed" value={q} disabled={!speciesId}
            onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); set("breed", e.target.value); setOpen(true); }}
            placeholder={speciesId ? "Search breed…" : "Select species first"}
            className="w-full rounded-md border border-slate-200 bg-white pl-8 pr-7 py-2 text-sm min-h-[44px] disabled:bg-slate-50" />
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        </div>
        {open && speciesId && breeds.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="breedselect-options">
            {breeds.map((b) => (
              <button type="button" key={b.id} data-testid={`breedselect-opt-${b.id}`} onClick={() => pickBreed(b)}
                className={`block w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 ${b.generic ? "text-slate-500 italic" : "text-slate-700"}`}>
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
